import { isEmpty, noop } from '@sgme/fp';
import type { AvailableTag, ISendMailRequest } from 'apiConnector/life-ring';
import { lifeRingApiConnector } from 'apiConnector/life-ring';
import classNames from 'classnames';
import { IcMultiplePicker } from 'components/shared/IcPicker/IcMultiplePicker';
import { FormattedMessage } from 'components/shared/Intl';
import { LightAlert } from 'components/shared/LightAlert';
import { SgTagsPicker } from 'components/shared/Tags';
import { TextArea } from 'components/shared/TextField/TextArea';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { useAppSelector } from 'hooks/useAppSelector';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { buildUserNotification, type UserNotifier } from 'hooks/useUserNotification';
import type { IntlKey } from 'locales';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import { Subscription } from 'rxjs';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import styled from 'styled-components';
import { httpClient } from 'utils/http/httpClient';

import { useFetchHelpCenterAvailableTags } from '../../../../hooks/use-fetch-help-center-tags';
import type { LifeRingModalStateProps } from './LifeRingModal.connect';
import { lifeRingModalMapStateToProps } from './LifeRingModal.connect';
import { Screenshot } from './Screenshot/Screenshot';

const StyledModal = styled(Modal)`
  > .modal-content {
    max-height: none;
  }
`;

interface OwnProps {
  isOpened: boolean;
  onExited: () => void;
  onClose: () => void;
  takeScreenshot: () => void;
  onScreenshotUpdated: (screenshot: HTMLCanvasElement) => void;
  onScreenshotReverted: () => void;
  screenshot?: HTMLCanvasElement;
  defaultMessage?: string;
  smartQuotes?: ISendMailRequest['smartQuotes'];
}
export type LifeRingModalProps = OwnProps & LifeRingModalStateProps & UserNotifier;

const Component: FC<LifeRingModalProps> = ({
  isOpened,
  externalQuoteId,
  quoteId,
  pricerId,
  orderId,
  isinCode,
  fileCode,
  productId,
  tradeId,
  dealId,
  productKey,
  currentRoute,
  contactIds,
  negotiationId,
  basketOptimizerId,
  basketOptimizerError,
  batchColumns,
  onExited,
  onClose,
  onScreenshotUpdated = noop,
  onScreenshotReverted,
  takeScreenshot,
  workflowErrors,
  screenshot,
  success,
  error,
  defaultMessage = '',
  smartQuotes,
}) => {
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [isMailSendingInProgress, setIsMailSendingInProgress] = useState<boolean>(false);
  const [isMailSent, setIsMailSent] = useState<boolean>(false);
  const [userMessage, setUserMessage] = useState<string>(defaultMessage);
  const [selectedTags, setSelectedTags] = useState<readonly string[]>([]);
  const [isTagDefaulted, setIsTagDefaulted] = useState<boolean>(false);
  const [lifeRingAPISubscription, setLifeRingAPISubscription] = useState<Subscription>(Subscription.EMPTY);
  const isInternal = useIsInternalUser();
  const [availableTags, setAvailableTags] = useState<readonly AvailableTag[]>([]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[] | undefined>(contactIds);

  const { formatMessage } = useIntl();

  useEffect(() => {
    return function cleanup() {
      if (lifeRingAPISubscription && !lifeRingAPISubscription.closed) {
        lifeRingAPISubscription.unsubscribe();
      }
    };
  }, [lifeRingAPISubscription]);

  useEffect(() => {
    if (selectedTags.length === 0 && availableTags?.length) {
      const defaultTag = availableTags.find(tag => tag.default === true);
      if (defaultTag) {
        setSelectedTags([defaultTag.key]);
        setIsTagDefaulted(true);
      }
    }
  }, [selectedTags, availableTags]);

  const resetState = useCallback(() => {
    setIsDrawing(false);
    setIsMailSendingInProgress(false);
    setIsMailSent(false);
    setUserMessage('');
    setSelectedTags([]);
  }, []);

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
    resetState();
  }, [onClose, resetState]);

  const handleScreenshotChanged = useCallback(
    (screenshot: HTMLCanvasElement) => {
      onScreenshotUpdated?.(screenshot);
      setIsDrawing(false);
    },
    [onScreenshotUpdated],
  );

  const handleScreenshotDrawing = useCallback(() => {
    setIsDrawing(true);
  }, []);

  const handleScreenshotPreview = useCallback(() => {
    setIsDrawing(false);
  }, []);

  const handleScreenshotRevert = useCallback(() => {
    if (onScreenshotReverted) {
      onScreenshotReverted();
    }
  }, [onScreenshotReverted]);

  const isDisabled =
    (isInternal && isEmpty(selectedTags)) || isDrawing || isMailSendingInProgress || !userMessage || !screenshot;

  const handleTakeScreenshot = useCallback(() => {
    setTimeout(() => {
      takeScreenshot();
    }, 100);
  }, [takeScreenshot]);

  const handleMailSentWihSuccess = useCallback(() => {
    setIsMailSent(true);
    success({ title: { id: 'toast.helpRequest' }, content: [{ id: 'lifeRing.helpRequestSentWithSuccess' }] });
  }, [success]);

  const handleMailSentWithError = useCallback(() => {
    setIsMailSendingInProgress(false);
    error({
      title: { id: 'toast.helpRequest' },
      content: [{ id: 'lifeRing.helpRequestSentWithError' }],
      closeAutomatically: false,
    });
  }, [error]);

  const screenshotInlineImage = useMemo(() => {
    const maxSize = 600 * 1024; // 1 Megabyte max size for http payload
    if (!screenshot) {
      return undefined;
    }

    const screenshotLossless = screenshot.toDataURL();
    if (screenshotLossless.length < maxSize) {
      return screenshotLossless;
    }
    const screenshotLowQuality = screenshot.toDataURL('image/jpeg', 0.5);
    if (screenshotLowQuality.length < maxSize) {
      return screenshotLowQuality;
    }

    return undefined;
  }, [screenshot]);

  const sendHelpRequest = useCallback(() => {
    setIsMailSendingInProgress(true);
    const request: ISendMailRequest = {
      userMessages: [userMessage],
      notifications: selectedContactIds,
      tags: isInternal ? selectedTags : [],
      screenShot: screenshotInlineImage,
      currentRoute,
      errors: workflowErrors,
      fileCode,
      isinCode,
      productId,
      tradeId,
      orderId,
      pricerId,
      productKey,
      quoteId,
      batchColumns,
      dealId,
      externalQuoteId,
      negotiationId,
      basketOptimizerId,
      basketOptimizerError,
      width: screenshot?.width,
      height: screenshot?.height,
      smartQuotes,
    };

    const currentSubscription = lifeRingApiConnector(httpClient)
      .sendMail(request)
      .subscribe({ next: handleMailSentWihSuccess, error: handleMailSentWithError });
    setLifeRingAPISubscription(currentSubscription);

    logAnalytics.trackEvent('Workspace', 'Help request send request');
  }, [
    currentRoute,
    externalQuoteId,
    negotiationId,
    basketOptimizerId,
    basketOptimizerError,
    batchColumns,
    fileCode,
    handleMailSentWihSuccess,
    handleMailSentWithError,
    isInternal,
    isinCode,
    orderId,
    pricerId,
    productId,
    tradeId,
    dealId,
    productKey,
    quoteId,
    screenshot?.height,
    screenshot?.width,
    screenshotInlineImage,
    selectedContactIds,
    selectedTags,
    userMessage,
    workflowErrors,
    smartQuotes,
  ]);

  const handleTagsSelectionChanged = useCallback((tags: readonly string[]) => {
    setSelectedTags(tags);
    setIsTagDefaulted(false);
  }, []);

  const handleUserMessageChanged = useCallback((userMessage: string) => {
    setUserMessage(userMessage);
  }, []);

  const handleContactsIdChanged = useCallback((contactIds: string[]) => {
    setSelectedContactIds(contactIds);
  }, []);

  const isTagRequiredWarning = isInternal && !isEmpty(availableTags) && isEmpty(selectedTags);
  const isUserMessageRequiredWarning = !userMessage?.trim();

  return (
    <StyledModal
      backdrop="static"
      dialogClassName="life-ring-modal-component"
      fade
      show={isOpened}
      onHide={onExited}
      onShow={handleTakeScreenshot}
      size="xl"
    >
      {isOpened && (
        <div data-e2e="LifeRing-modal">
          <ModalHeader>
            <ModalTitle as="h5">
              <FormattedMessage id="lifeRing.helpRequest" />
            </ModalTitle>
          </ModalHeader>

          <ModalBody className="pb-0" style={{ maxHeight: '70vh' }}>
            {isInternal && (
              <LifeRingInternalTagsPicker
                handleTagsLoaded={setAvailableTags}
                handleTagsSelectionChanged={handleTagsSelectionChanged}
                isTagDefaulted={isTagDefaulted}
                isTagRequiredWarning={isTagRequiredWarning}
                selectedTags={selectedTags}
              />
            )}
            {isInternal && (
              <LifeRingInternalContactsPicker
                contactIds={selectedContactIds}
                onContactIdsChanged={handleContactsIdChanged}
              />
            )}
            <TextArea
              disabled={isMailSendingInProgress || isMailSent}
              onValueChanged={handleUserMessageChanged}
              value={userMessage}
              className={classNames('form-control mt-4', { 'is-invalid': isUserMessageRequiredWarning })}
              rows={4}
              placeholder={formatMessage({ id: 'lifeRing.helpRequestComment' })}
            />
            {isUserMessageRequiredWarning && (
              <div className="invalid-feedback">
                <em className="icon">close</em>
                <FormattedMessage id="lifeRing.mandatoryUserMessage" />
              </div>
            )}
            <Screenshot
              isFrozen={isMailSendingInProgress || isMailSent}
              onChange={handleScreenshotChanged}
              onDrawing={handleScreenshotDrawing}
              onPreview={handleScreenshotPreview}
              onRevert={handleScreenshotRevert}
              screenshot={screenshot}
            />
          </ModalBody>

          <ModalFooter>
            <button className="btn btn-lg btn-default" data-e2e="LifeRing-modal-close-button" onClick={handleOnClose}>
              <FormattedMessage id="close" />
            </button>
            {!isMailSent && (
              <OverlayTrigger
                show={isDrawing ? undefined : false}
                overlay={
                  <Tooltip>
                    <FormattedMessage id="lifeRing.pleaseValidateOrDiscardScreenshotAnnotationsBeforeSending" />
                  </Tooltip>
                }
              >
                <span>
                  <button className="btn btn-lg btn-primary" disabled={isDisabled} onClick={sendHelpRequest}>
                    {isMailSendingInProgress ? (
                      <FormattedMessage id="sendingInProgress" />
                    ) : (
                      <FormattedMessage id="lifeRing.sendHelpRequest" />
                    )}
                  </button>
                </span>
              </OverlayTrigger>
            )}
          </ModalFooter>
        </div>
      )}
    </StyledModal>
  );
};

interface LifeRingInternalTagsPickerProps {
  selectedTags: readonly string[];
  isTagRequiredWarning: boolean;
  isTagDefaulted: boolean;
  handleTagsLoaded(tags: readonly AvailableTag[]): void;
  handleTagsSelectionChanged(tags: readonly string[]): void;
}

function LifeRingInternalTagsPicker({
  selectedTags,
  isTagRequiredWarning,
  isTagDefaulted,
  handleTagsLoaded,
  handleTagsSelectionChanged,
}: LifeRingInternalTagsPickerProps) {
  const { formatMessage } = useIntl();
  const { status: fetchStatus, data: availableTags, error: fetchError, onRetry } = useFetchHelpCenterAvailableTags();
  const apiErrorMessage = fetchError !== 'unauthorized' ? (fetchError ?? undefined) : undefined;

  useEffect(() => {
    if (fetchStatus === 'success' && !isEmpty(availableTags)) {
      handleTagsLoaded(availableTags);
    }
  }, [fetchStatus, availableTags, handleTagsLoaded]);
  return (
    <>
      {fetchStatus === 'loading' && <TagsPickerSkeleton className="w-100" />}
      {fetchStatus === 'success' && (
        <>
          <div className="mb-2" data-e2e="tags-selector">
            <SgTagsPicker
              id="help-request-tags"
              placeholder={formatMessage({ id: 'lifeRing.topic.placeHolder' })}
              available={availableTags ?? []}
              onSelectionKeysChanged={handleTagsSelectionChanged}
              selection={selectedTags}
              singleSelect
              e2e="help-request-tags"
              className={classNames('form-control p-0', {
                'is-invalid': isTagRequiredWarning,
              })}
            />
            {isTagRequiredWarning && (
              <div className="invalid-feedback">
                <em className="icon">close</em>
                <FormattedMessage id="lifeRing.mandatoryTopic" />
              </div>
            )}
            {isTagDefaulted && (
              <small className="form-text text-info">
                <em className="icon me-1">info</em>
                <FormattedMessage id="lifeRing.topicDefaulted" />
              </small>
            )}
          </div>
        </>
      )}
      {fetchStatus === 'error' && <Error onRetry={onRetry} error={apiErrorMessage} />}
    </>
  );
}

interface LifeRingInternalContactsPickerProps {
  contactIds?: string[];
  onContactIdsChanged: (value: string[]) => void;
}
function LifeRingInternalContactsPicker({ contactIds, onContactIdsChanged }: LifeRingInternalContactsPickerProps) {
  return (
    <div className="mb-1">
      <IcMultiplePicker
        withLabel
        id={`help-request-ic-picker`}
        labelTranslationKey="icMultiplePicker.helpRequestNotifications.title"
        placeholderTranslationKey="icMultiplePicker.helpRequestNotifications.placeholder"
        selectedIds={contactIds}
        onChange={contacts => onContactIdsChanged(contacts.map(contact => contact.id))}
      />
    </div>
  );
}

export function useDispatchToProps(): UserNotifier {
  const dispatch = useThunkDispatch();
  return buildUserNotification(dispatch);
}

export function useMapStateToProps(ownProps: OwnProps) {
  return useAppSelector(state => lifeRingModalMapStateToProps({ ...ownProps, ...state }));
}

export function LifeRingModal(props: OwnProps) {
  const stateProps = useMapStateToProps(props);
  const dispatchProps = useDispatchToProps();

  return <Component {...props} {...stateProps} {...dispatchProps} />;
}

const TagsPickerSkeleton: FC<{ className?: string }> = ({ className }) => (
  <div className={classNames('bg-lvl1', className)}>
    <Skeleton className="w-100" />
  </div>
);

const WarningTooltip = styled(Tooltip)`
  & > .tooltip-arrow::before {
    border-top-color: #da7400;
  }
`;

const Error: FC<{
  className?: string;
  error?: ApiErrorMessage;
  onRetry?: () => void;
}> = ({ className, error, onRetry }) => {
  const { formatMessage } = useIntl();
  return (
    <LightAlert
      severity="secondary"
      e2e="help-request-get-availaible-tags-error"
      className={classNames('w-100 p-0 py-0 px-5 mb-3', className)}
    >
      <OverlayTrigger
        show={error ? undefined : false}
        overlay={
          <WarningTooltip className="tooltip-warning">
            {error ? formatMessage({ id: error.message as IntlKey }) : null}
          </WarningTooltip>
        }
      >
        <span data-e2e="help-request-get-available-tags-error-message" className={classNames({ clickable: !!error })}>
          {formatMessage({ id: 'lifeRing.fetchData.error' })}
        </span>
      </OverlayTrigger>
      <em className="flex-grow-1" />
      {onRetry && (
        <button
          className="btn btn-sm btn-outline-secondary float-end"
          onClick={onRetry}
          data-e2e="help-request-get-availaible-tags-error-retry-button"
        >
          {formatMessage({
            id: 'lifeRing.fetchData.retry',
            defaultMessage: 'retry',
          })}
        </button>
      )}
    </LightAlert>
  );
};
